import log from 'loglevel';
import {
  Events,
  State,
  Source,
  QualityLevel,
  TextTrack,
  AudioTrack,
  VideoTrack,
  NotificationAction,
  StateChangedEvents,
  QualityLevelEvents,
  AudioTrackEvents,
  VideoTrackEvents,
  CurrentTimeEvents,
  TextTrackEvents,
  Responsive,
  QualityLevelKind,
  SizePlayerEvents,
  ToggleControlsChangedEvents,
  ClickAction
} from '../types/player';
import { Error } from '../types/error';
import Sdk from './Sdk';
import Controls from './optionsManager/Controls';

//TODO: refacto events in types and class events, cause there is issue with doc generation

/**
 * Description of PlayerEvents class
 * @class PlayerEvents
 * @hideconstructor
 */
class PlayerEvents {
  sdk: Sdk;

  constructor(sdk: Sdk) {
    this.sdk = sdk;
  }

  isInit() {
    /**
     * on('init') PlayerEvents
     *
     * @event PlayerEvents#init
     * @example
     * ```js
     * player.on('init', () => {
     *    console.log('player is init');
     * });
     * ```
     */
    this.sdk.emit(Events.INIT);
  }

  isReady() {
    /**
     * on('ready') PlayerEvents
     *
     * @event PlayerEvents#ready
     * @example
     * ```js
     * player.on('ready', () => {
     *    console.log('player is ready');
     * });
     * ```
     */
    this.sdk.emit(Events.READY);
  }

  play() {
    /**
     * on('play') PlayerEvents
     *
     * @event PlayerEvents#play
     * @example
     * ```js
     * player.on('play', () => {
     *    console.log('play');
     * });
     * ```
     */
    this.sdk.emit(Events.PLAY);
  }

  pause() {
    /**
     * on('pause') PlayerEvents
     *
     * @event PlayerEvents#pause
     * @example
     * ```js
     * player.on('pause', () => {
     *    console.log('pause');
     * });
     * ```
     */
    this.sdk.emit(Events.PAUSE);
  }

  end() {
    /**
     * on('end') PlayerEvents
     *
     * @event PlayerEvents#end
     * @example
     * ```js
     * player.on('end', () => {
     *    console.log('end');
     * });
     * ```
     */
    this.sdk.emit(Events.END);
  }

  stateChanged(previousState: State, newState: State) {
    /**
     * on('stateChanged') PlayerEvents
     *
     * @event PlayerEvents#stateChanged
     * @returns {StateChangedEvents}
     * @example
     * ```js
     * player.on('stateChanged', (state: StateChangedEvents) => {
     *    console.log(state.previousState, ' -> ', state.newState);
     * });
     * ```
     */
    log.debug(previousState, ' -> ', newState);
    this.sdk.emit(Events.STATE_CHANGED, {
      previousState,
      newState,
    } as StateChangedEvents);
  }

  currentTime(
    currentTime: number,
    duration: number,
    currentTimeInPercentage: number,
  ) {
    /**
     * on('currentTime') event
     *
     * @event PlayerEvents#currentTime
     * @returns {CurrentTimeEvents}
     * @example
     * ```js
     * player.on('currentTime', (time: CurrentTimeEvents) => {
     *    console.log(time.currentTime, time.duration, time.currentTimeInPercentage);
     * });
     * ```
     */
    this.sdk.emit(Events.CURRENT_TIME, {
      currentTime,
      duration,
      currentTimeInPercentage,
    } as CurrentTimeEvents);
  }

  progress(percentage: number) {
    /**
     * on('progress') event
     *
     * @event PlayerEvents#progress
     * @returns {number}
     * @example
     * ```js
     * player.on('progress', (percentage: number) => {
     *   console.log(percentage);
     * });
     * ```
     * */
    this.sdk.emit(Events.PROGRESS, percentage);
  }

  muted(muted: boolean) {
    /**
     * on('muted') event
     *
     * @event PlayerEvents#muted
     * @returns {boolean}
     * @example
     * ```js
     * player.on('muted', (muted: boolean) => {
     *   console.log(muted);
     * });
     * ```
     * */
    this.sdk.emit(Events.MUTED, muted);
  }

  seeking() {
    /**
     * on('seeking') event
     *
     * @event PlayerEvents#seeking
     * @example
     * ```js
     * player.on('seeking', () => {
     *    console.log('seeking');
     * });
     * ```
     */
    this.sdk.emit(Events.SEEKING);
  }

  seeked() {
    /**
     * on('seeked') event
     *
     * @event PlayerEvents#seeked
     * @example
     * ```js
     * player.on('seeked', () => {
     *    console.log('seeked');
     * });
     * ```
     */
    this.sdk.emit(Events.SEEKED);
  }

  volumeChanged(volume: number) {
    /**
     * on('volumeChanged') event
     *
     * @event PlayerEvents#volumeChanged
     * @returns {number}
     * @example
     * ```js
     * player.on('volumeChanged', (volume: number) => {
     *    console.log(volume);
     * });
     * ```
     */
    this.sdk.emit(Events.VOLUME_CHANGED, volume);
  }

  sourceChanged(source: Source) {
    /**
     * on('sourceChanged') event
     *
     * @event PlayerEvents#sourceChanged
     * @returns {Source}
     * @example
     * ```js
     * player.on('sourceChanged', (source: Source) => {
     *    console.log(source);
     * });
     * ```
     */
    this.sdk.emit(Events.SOURCE_CHANGED, { source });
  }

  qualityLevelChanged(
    qualityLevel: QualityLevel,
    index: number,
    isAutoQualityLevel: boolean,
    kind: QualityLevelKind = QualityLevelKind.VIDEO,
  ) {
    /**
     * on('qualityLevelChanged') event
     *
     * @event PlayerEvents#qualityLevelChanged
     * @returns {QualityLevelEvents}
     * @example
     * ```js
     * player.on('qualityLevelChanged', (qualityLevel: QualityLevelEvents) => {
     *    console.log(qualityLevel.qualityLevel, qualityLevel.index, qualityLevel.isAutoQualityLevel, qualityLevel.mediaType);
     * });
     * ```
     */
    this.sdk.emit(Events.QUALITYLEVEL_CHHANGED, {
      qualityLevel,
      index,
      isAutoQualityLevel,
      kind,
    } as QualityLevelEvents);
  }

  durationChanged(duration: number) {
    /**
     * on('durationChanged') event
     *
     * @event PlayerEvents#durationChanged
     * @returns {number}
     * @example
     * ```js
     * player.on('durationChanged', (duration: number) => {
     *    console.log(duration);
     * });
     * ```
     */
    this.sdk.emit(Events.DURATION_CHANGED, { duration });
  }

  textTrackChanged(textTrack: TextTrack, index: number) {
    /**
     * on('textTrackChanged') event
     *
     * @event PlayerEvents#textTrackChanged
     * @returns {TextTrackEvents}
     * @example
     * ```js
     * player.on('textTrackChanged', (TextTrack: TextTrackEvents) => {
     *    console.log(TextTrack.TextTrack, TextTrack.index);
     * });
     * ```
     */
    this.sdk.emit(Events.TEXTTRACK_CHANGED, {
      textTrack,
      index,
    } as TextTrackEvents);
  }

  audioTrackChanged(audioTrack: AudioTrack, index: number) {
    /**
     * on('audioTrackChanged') event
     *
     * @event PlayerEvents#audioTrackChanged
     * @returns {AudioTrackEvents}
     * @example
     * ```js
     * player.on('audioTrackChanged', (audioTrack: AudioTrackEvents) => {
     *    console.log(audioTrack.audioTrack, audioTrack.index);
     * });
     * ```
     */
    this.sdk.emit(Events.AUDIOTRACK_CHANGED, {
      audioTrack,
      index,
    } as AudioTrackEvents);
  }

  videoTrackChanged(videoTrack: VideoTrack, index: number) {
    /**
     * on('videoTrackChanged') event
     *
     * @event PlayerEvents#videoTrackChanged
     * @returns {VideoTrackEvents}
     * @example
     * ```js
     * player.on('videoTrackChanged', (videoTrack: VideoTrackEvents) => {
     *    console.log(videoTrack.videoTrack, videoTrack.index);
     * });
     * ```
     */
    this.sdk.emit(Events.VIDEOTRACK_CHANGED, {
      videoTrack,
      index,
    } as VideoTrackEvents);
  }

  playbackRateChanged(playbackRate: number) {
    /**
     * on('playbackRateChanged') event
     *
     * @event PlayerEvents#playbackRateChanged
     * @returns {number}
     * @example
     * ```js
     * player.on('playbackRateChanged', (playbackRate: number) => {
     *    console.log(playbackRate);
     * });
     * ```
     */
    this.sdk.emit(Events.PLAYBACKRATE_CHANGED, playbackRate);
  }

  fullscreenChanged(fullscreen: boolean) {
    /**
     * on('fullscreenChanged') event
     *
     * @event PlayerEvents#fullscreenChanged
     * @returns {boolean}
     * @example
     * ```js
     * player.on('fullscreenChanged', (fullscreen: boolean) => {
     *    console.log(fullscreen);
     * });
     * ```
     */
    this.sdk.emit(Events.FULLSCREEN_CHANGED, fullscreen);
  }

  toggleControls() {    
    this.sdk.emit(Events.TOGGLE_CONTROLS);
  }

  seekControlIntervalChanged(interval: number) {
    /**
     * on('seekControlIntervalChanged') event
     *
     * @event PlayerEvents#seekControlIntervalChanged
     * @returns {number}
     * @example
     * ```js
     * player.on('seekControlIntervalChanged', (interval: number) => {
     *    console.log(interval);
     * });
     * ```
     */
    this.sdk.emit(Events.SEEK_CONTROL_INTERVAL_CHANGED, interval);
  }

  pipStatusChanged(pip: boolean) {
    /**
     * on('pipChanged') event
     *
     * @event PlayerEvents#pipChanged
     * @returns {boolean}
     * @example
     * ```js
     * player.on('pipChanged', (pip: boolean) => {
     *    console.log(pip);
     * });
     * ```
     */
    this.sdk.emit(Events.PIP_CHANGED, pip);
  }

  airPlayAvailabilityChanged(available: boolean) {
    /**
     * on('airPlayAvailabilityChanged') event
     *
     * @event PlayerEvents#airPlayAvailabilityChanged
     * @returns {boolean}
     * @example
     * ```js
     * player.on('airPlayAvailabilityChanged', (available: boolean) => {
     *    console.log(available);
     * });
     * ```
     */
    this.sdk.emit(Events.AIRPLAY_AVAILABILITY_CHANGED, available);
  }

  notificationAction(action: NotificationAction) {
    this.sdk.emit(Events.NOTIFICATION_ACTION, action);
  }

  error(error: Error) {
    /**
     * on('error') event
     *
     * @event PlayerEvents#error
     * @returns {Error}
     * @example
     * ```js
     * player.on('error', (error: Error) => {
     *    console.log(error);
     * });
     * ```
     */
    this.stateChanged(this.sdk.getCurrentState(), State.ERROR);
    this.sdk.emit(Events.ERROR, error);
  }

  toggleControlMenu(control: string) {
    /**
     * on('toggleControlMenu') event
     * Triggered when the user clicks on a control menu
     *
     * @event PlayerEvents#toggleControlMenu
     * @returns {string}
     * @example
     * ```js
     * player.on('toggleControlMenu', (menuName: string) => {
     *    console.log(menuName);
     * });
     * ```
     */
    this.sdk.emit(Events.TOGGLE_CONTROL_MENU, control);
  }

  clickControl(control: string) {
    /**
     * on('clickControl') event
     *
     * @event PlayerEvents#clickControl
     * @example
     * ```js
     * player.on('clickControl', (control: string) => {
     *    console.log(control);
     * });
     * ```
     */
    this.sdk.emit(Events.CLICK_CONTROL, control);
  }

  toggleControlsChanged(status: string) {
    /**
     * on('toggleControlsChanged') event
     * triggered when the control bar visibility change
     *
     * @event PlayerEvents#toggleControlsChanged
     * @returns {ToggleControlsChangedEvents}
     * @example
     * ```js
     * player.on('toggleControlsChanged', (status: string) => {
     *    console.log(status);
     * });
     * ```
     */
    this.sdk.emit(Events.TOGGLE_CONTROLS_CHANGED, status);
  }

  resize(size: string, width: number, height: number) {
    /**
     * on('resize') event
     * @event PlayerEvents#resize
     * @returns {SizePlayerEvents}
     * @example
     * ```js
     * player.on('resize', (sizePlayer: SizePlayerEvents) => {
     *   console.log(size);
     * });
     */
    this.sdk.emit(Events.CHANGE_SIZE, {
      size,
      width,
      height,
    } as SizePlayerEvents);
  }

  destroy() {
    /**
     * on('destroy') event
     * @event PlayerEvents#destroy
     * @example
     * ```js
     * player.on('destroy', () => {
     *   console.log('destroy');
     * });
     */
    this.sdk.emit(Events.DESTROY);
  }

  mouseOver(over: boolean) {
    this.sdk.emit(Events.MOUSE_OVER, over);
    if(this.sdk.options().getControls().getSettings().click && this.sdk.options().getControls().getSettings().click == ClickAction.CONTROLS)
      return;
    this.toggleControlsChanged(
      over
        ? ToggleControlsChangedEvents.SHOW
        : ToggleControlsChangedEvents.HIDE,
    );
  }

  playerVisible(visible: boolean) {
    this.sdk.emit(Events.PLAYER_VISIBLE, visible);
  }

  bigButtonChanged(button: string | null) {
    this.sdk.emit(Events.BIG_BUTTON_CHANGED, button);
  }
}
export { PlayerEvents };
export type {
  ToggleControlsChangedEvents,
  StateChangedEvents,
  QualityLevelEvents,
  CurrentTimeEvents,
  TextTrackEvents,
  AudioTrackEvents,
  VideoTrackEvents,
};
